<template>
  <div class="column gap-1 input-wrapper" :class="{ disabled }">
    <label
      v-if="label"
      class="body xs font-regular row gap-1 align-center input-label"
      :for="fieldId"
    >
      {{ label }}
      <CustomTooltip v-if="additionalInfo" :message="additionalInfo">
        <AppIcon name="info" size="body-s" class="additional-info" />
      </CustomTooltip>
    </label>
    <input
      :id="fieldId"
      :type="inputType"
      class="input-field"
      :placeholder="placeholderText"
      :value="internalValue"
      @input="updateValue"
      :disabled="disabled"
    />
    <div v-if="error && errorMessage" class="row gap-1 error-message">
      <AppIcon name="info" size="body-s" />
      <p class="body xs font-regular">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import CustomTooltip from '@/components/common/tooltip/CustomTooltip.vue';
import { InputType } from '@/models/input-type';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  label?: string;
  placeHolder?: string;
  placeholder?: string;
  modelValue?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  additionalInfo?: string;
  disabled?: boolean;
  type?: InputType;
  id?: string;
}>();

const emit = defineEmits(['update:modelValue']);
const internalValue = ref(props.modelValue ?? '');
const inputType = props.type ?? InputType.Text;
const uniqueId = `text-field-${Math.random().toString(36).substring(2, 9)}`;
const fieldId = computed(() => props.id || uniqueId);

const placeholderText = computed(
  () => props.placeholder ?? props.placeHolder ?? ''
);

function updateValue(e: Event) {
  const t = e.target as HTMLInputElement;
  internalValue.value = t.value;
  emit('update:modelValue', internalValue.value);
}

watch(
  () => props.modelValue,
  (v) => {
    internalValue.value = v ?? '';
  }
);
</script>

<style scoped lang="scss">
.input-wrapper {
  text-align: start;
  width: 100%;
  margin-bottom: $space-2;

  &:focus-within {
    outline: none;
    .input-field {
      border: 2px solid $border-color-focus;
      transition: 0.2s ease;
    }
  }

  &.disabled {
    color: $color-text-disabled !important;
    .input-field {
      cursor: not-allowed;
      border: 1px solid $border-color-disabled;
      background-color: $border-color-disabled;
    }
  }
}

.input-label {
  .required {
    color: $border-color-error;
  }
}

.input-field {
  width: 100%;
  border: 1px solid $border-color-base;
  border-radius: $radius-3;
  padding: $space-5 $space-3;
  color: $color-neutral-700;
  font-weight: $fw-regular;
  font-size: $body-s-font-size;
  background-color: $color-neutral-0;
  user-select: none;
  cursor: text;

  &:hover {
    border: 1px solid $border-color-hover;
    transition: 0.2s ease;
  }

  &:focus {
    outline: none;
    border: 2px solid $border-color-focus;
  }

  &.error {
    border: 1px solid $border-color-error !important;
    transition: 0.2s ease;
  }

  &::-webkit-input-placeholder {
    color: $color-neutral-400;
    opacity: 1;
  }
  &:-ms-input-placeholder {
    color: $color-neutral-400;
    opacity: 1;
  }
  &::placeholder {
    color: $color-neutral-400;
    opacity: 1;
  }
}

.error-message {
  color: $border-color-error;
}

.additional-info {
  color: $border-color-base;
}
</style>
